import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import supabase from '../../../../supabaseClient';
import CalendarMonth from './CalendarMonth';
import CalendarWeek from './CalendarWeek';
import CalendarDay from './CalendarDay';
import CalendarCategory from './CalendarCategory';
import CalendarAddNewTask from './CalendarAddNewTask';
import CalendarEventDetail from './CalendarEventDetail';
import CalendarFilter from './CalendarFilter';

const MONTH_NAMES = ['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec','Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function parseISO(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function toISO(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function Calendar() {
  const [currentDate, setCurrentDate] = useState(todayISO());
  const [viewMode, setViewMode] = useState('month');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterEmployee, setFilterEmployee] = useState('ALL');
  const [modal, setModal] = useState(null); // null | 'add' | 'categories' | 'detail'
  const [addPresetDate, setAddPresetDate] = useState(currentDate);
  const [categoryVersion, setCategoryVersion] = useState(0);
  const [eventsVersion, setEventsVersion] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [idConfig, setIdConfig] = useState(null);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data } = await supabase.from('profiles').select('id_configuration').eq('id', session.user.id).single();
        if (data) setIdConfig(data.id_configuration);
      }
    };
    init();
  }, []);

  function openAddModal(dateISO) {
    setAddPresetDate(dateISO || currentDate);
    setModal('add');
  }

  function openDetail(event) {
    setSelectedEvent(event);
    setModal('detail');
  }

  function goPrev() {
    const d = parseISO(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() - 1);
    else if (viewMode === 'week') d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCurrentDate(toISO(d));
  }
  function goNext() {
    const d = parseISO(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + 1);
    else if (viewMode === 'week') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCurrentDate(toISO(d));
  }
  function goToday() { setCurrentDate(todayISO()); }

  const d = parseISO(currentDate);
  const periodLabel = viewMode === 'month'
    ? `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`
    : currentDate;

  return (
    <Box sx={{ mt: 5, mx: 'auto', px: { xs: 2, md: 4 }, maxWidth: 1400 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        background: '#fff', border: '1px solid #f0f0f0', borderRadius: '16px', padding: '16px 20px', mb: 3,
      }}>
        <button onClick={goPrev} style={navBtnStyle}>‹</button>
        <div style={{ fontSize: 17, fontWeight: 700, minWidth: 170, textAlign: 'center' }}>{periodLabel}</div>
        <button onClick={goNext} style={navBtnStyle}>›</button>
        <button onClick={goToday} style={{ ...navBtnStyle, width: 'auto', padding: '6px 12px', fontSize: 12, color: '#705A5E' }}>Dziś</button>

        <Box sx={{ display: 'flex', gap: 0.5, background: '#FDFBF7', borderRadius: '8px', padding: '4px', ml: 1 }}>
          {['month', 'week', 'day'].map((mode) => (
            <button key={mode} onClick={() => setViewMode(mode)} style={{
              border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: viewMode === mode ? '#6B1D2F' : 'transparent',
              color: viewMode === mode ? '#fff' : '#705A5E',
            }}>
              {mode === 'month' ? 'Miesiąc' : mode === 'week' ? 'Tydzień' : 'Dzień'}
            </button>
          ))}
        </Box>

        <CalendarFilter
          filterCategory={filterCategory} setFilterCategory={setFilterCategory}
          filterEmployee={filterEmployee} setFilterEmployee={setFilterEmployee}
          categoryVersion={categoryVersion}
          idConfig={idConfig}
        />

        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <button onClick={() => setModal('categories')} style={outlineBtnStyle}>Kategorie</button>
          <button onClick={() => openAddModal()} style={filledBtnStyle}>+ Nowa akcja</button>
        </Box>
      </Box>

      {viewMode === 'month' && (
        <CalendarMonth
          currentDate={currentDate}
          filterCategory={filterCategory}
          filterEmployee={filterEmployee}
          onAddClick={openAddModal}
          onEventClick={openDetail}
          eventsVersion={eventsVersion}
          idConfig={idConfig} />
      )}
      {viewMode === 'week' && (
        <CalendarWeek
          currentDate={currentDate}
          filterCategory={filterCategory}
          filterEmployee={filterEmployee}
          onAddClick={openAddModal}
          onEventClick={openDetail}
          eventsVersion={eventsVersion}
          idConfig={idConfig} />
      )}
      {viewMode === 'day' && (
        <CalendarDay
          currentDate={currentDate}
          filterCategory={filterCategory}
          filterEmployee={filterEmployee}
          onAddClick={openAddModal}
          onEventClick={openDetail}
          eventsVersion={eventsVersion}
          idConfig={idConfig} />
      )}

      {modal === 'categories' && (
        <CalendarCategory onClose={() => { setModal(null); setCategoryVersion((v) => v + 1); }} idConfig={idConfig} />
      )}
      {modal === 'add' && (
        <CalendarAddNewTask
          onClose={() => { setModal(null); setEventsVersion((v) => v + 1); }}
          presetDate={addPresetDate}
          idConfig={idConfig} />
      )}
      {modal === 'detail' && (
        <CalendarEventDetail
          event={selectedEvent}
          onClose={() => { setModal(null); setSelectedEvent(null); setEventsVersion((v) => v + 1); }}
          idConfig={idConfig} />
      )}
    </Box>
  );
}

export default Calendar;

const navBtnStyle = { background: 'none', border: '1px solid #e3d9d9', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', color: '#6B1D2F', fontSize: 16 };
const outlineBtnStyle = { background: '#fff', border: '1px solid #e3d9d9', color: '#6B1D2F', fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, cursor: 'pointer' };
const filledBtnStyle = { background: '#6B1D2F', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, padding: '9px 18px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' };